import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  fetchChartEntriesWithStatus,
  createChartEntry,
  updateChartEntry,
  flagChartEntry,
  invalidateChartEntry,
  EntryType,
  CreateEntryPayload,
  ChartEntry,
  ChartMeta,
  EntryData
} from '../../../../services/patientmr/charting.service';
import { fetchMedications } from '../../../../services/patientmr/medications.service';
import { fetchVisitById } from '../../../../services/patientmr/visits.service';
import { defaultObservedTime, observedIsoFromTime, observedProblem, VisitWindow } from '../../../../utils/observedAt';
import { getApiErrorCode, getFieldErrors, hasApiErrorCode } from '../../../../utils/apiErrors';
import { Medication } from '../../../../dtos/patientmr/medication.dto';
import type { EntryVisibility } from '../../../../dtos/patientmr/charting';
import { convertEntryTypeToApi } from '../api-utils';
import {
  Entry,
  VitalFormData,
  MeasurementsFormData,
  MarFormData,
  IncidentFormData,
  FeedFormData,
  NoteFormData,
  WoundFormData,
  RespiratoryFormData,
  SeizureFormData,
  OutputFormData,
  SupervisoryFormData,
  TherapyFormData,
  AideTasksFormData
} from '../types';
import type { ChartVisitInfo } from '../ChartFormContext';
import {
  emptyFeed,
  emptyIncident,
  emptyMar,
  emptyMeasurements,
  emptyNote,
  emptyOutput,
  emptyRespiratory,
  emptySeizure,
  emptySupervisory,
  emptyTherapy,
  emptyAideTasks,
  emptyVitals,
  emptyWound,
  EntryFormBundle
} from '../model/entryForms';
import {
  buildEntryData,
  feedForm,
  incidentForm,
  marFollowUpForm,
  marForm,
  measurementsForm,
  noteForm,
  outputForm,
  respiratoryForm,
  seizureForm,
  summaryForm,
  supervisoryForm,
  therapyForm,
  aideTasksForm,
  vitalsForm,
  woundForm
} from '../model/entryPayload';
import { EntryErrors, validateEntryForm } from '../model/entryValidation';
import { emptyTemplateNote, noteTemplateFor, templateNoteForm, TemplateNoteFormData } from '../model/noteTemplates';
import { entryTimeMs, latestEntryOfType } from '../model/entryMeta';

// Newest first by observed time (Phase 18 M8), falling back to the entry timestamp
const sortEntriesByDateDesc = (entries: Entry[]): Entry[] =>
  [...entries].sort((a, b) => entryTimeMs(b) - entryTimeMs(a));

/** Phase 9C fields copied from the API entry onto the local Entry */
const integrityFields = (e: ChartEntry): Partial<Entry> => ({
  lateEntry: e.lateEntry,
  minutesAfterEnd: e.minutesAfterEnd,
  version: e.version,
  amended: e.amended,
  lastAmendment: e.lastAmendment,
  versions: e.versions,
  addendum: e.addendum,
  createdBy: e.createdBy,
  observedAt: e.observedAt,
  documentedAt: e.documentedAt,
  syncState: e.syncState,
  editVersion: e.editVersion
});

/**
 * Phase 18U U5: an error the entry form shows next to its fields (validation, observed time).
 * The chart shows these inline in the sheet and never as the modal error dialog.
 */
export const isInlineEntryError = (error: unknown): boolean => {
  if ((error as { inline?: boolean } | null)?.inline) return true;
  if (hasApiErrorCode(error, 'ENTRY_INVALID') || getApiErrorCode(error) === 'TIME_ORDER') return true;
  return Object.keys(getFieldErrors(error)).length > 0;
};

const inlineError = (message: string): Error => Object.assign(new Error(message), { inline: true });

/** Phase 18U U4: put the saved entry in the list now; the reload that follows reconciles it */
export const upsertEntry = (entries: Entry[], entry: Entry): Entry[] => {
  const rest = entries.filter((e) => String(e.id) !== String(entry.id));
  return sortEntriesByDateDesc([entry, ...rest]);
};

const toLocalEntry = (apiEntry: ChartEntry, visitId: string): Entry => ({
  id: apiEntry.id,
  type: apiEntry.type || apiEntry.entryType.toLowerCase(),
  date: apiEntry.date,
  provider: apiEntry.provider,
  visitId,
  data: apiEntry.data,
  lastEdited: apiEntry.lastEdited,
  flagged: apiEntry.flagged,
  invalidated: apiEntry.invalidated,
  invalidationInfo: apiEntry.invalidationInfo,
  ...integrityFields(apiEntry)
});

export const useChartingData = (patientId: string, activeVisitId?: string, visitWindow: VisitWindow = {}) => {
  // State for entries
  const [currentVisitEntries, setCurrentVisitEntries] = useState<Entry[]>([]);
  const [historicalVisitEntries, setHistoricalVisitEntries] = useState<Record<string | number, Entry[]>>({});
  const [hasActiveVisit, setHasActiveVisit] = useState<boolean>(!!activeVisitId);
  const [loadingEntries, setLoadingEntries] = useState<boolean>(false);
  // Phase 18U U4: the visit whose entries are on screen; reloading it never shows the spinner
  const loadedVisitRef = useRef<string | null>(null);
  // Phase 9C chart metadata (author, sign-off, return comment, reopen request) by visit id
  const [chartMetaByVisit, setChartMetaByVisit] = useState<Record<string, ChartMeta>>({});
  // Phase 18 M7: last GIVEN time per medication, by visit id
  const [lastGivenByVisit, setLastGivenByVisit] = useState<Record<string, Record<string, string>>>({});
  // Phase 18: visit type, discipline and times of the charted visits, by visit id
  const [visitInfoById, setVisitInfoById] = useState<Record<string, ChartVisitInfo>>({});

  // State for editing
  const [editingEntryId, setEditingEntryId] = useState<number | string | null>(null);
  const [inPlaceEditingEntryId, setInPlaceEditingEntryId] = useState<number | string | null>(null);
  const [selectedVisitId, setSelectedVisitId] = useState<string | number>('current');

  const chartVisitId = selectedVisitId === 'current' ? activeVisitId : String(selectedVisitId);
  const chartVisit = chartVisitId ? visitInfoById[chartVisitId] || null : null;
  // The window of the visit on screen: the caller's (offline workspace) or the fetched visit's
  const chartWindow: VisitWindow = useMemo(() => {
    if (selectedVisitId === 'current' && (visitWindow.startedAt || visitWindow.endedAt)) return visitWindow;
    return chartVisit ? { startedAt: chartVisit.startTime || null, endedAt: chartVisit.endTime || null } : {};
  }, [selectedVisitId, visitWindow, chartVisit]);
  const defaultClock = useCallback(() => defaultObservedTime(chartWindow), [chartWindow]);

  // Phase 16C (C7): when the care happened; MAR uses its administration time, seizure its start
  const [observedTime, setObservedTime] = useState<string>(() => defaultObservedTime(visitWindow));
  const [observedError, setObservedError] = useState<string | null>(null);
  // Phase 18 M1: 400 ENTRY_INVALID fields of the last save
  const [serverFieldErrors, setServerFieldErrors] = useState<EntryErrors>({});

  // Form data states
  const [vitalFormData, setVitalFormData] = useState<VitalFormData>(emptyVitals);
  const [patientMedications, setPatientMedications] = useState<Medication[]>([]);
  const [marFormData, setMarFormData] = useState<MarFormData>(() => emptyMar(defaultObservedTime(visitWindow)));
  const [feedFormData, setFeedFormData] = useState<FeedFormData>(emptyFeed);
  const [notesFormData, setNotesFormData] = useState<NoteFormData>(emptyNote);
  const [woundFormData, setWoundFormData] = useState<WoundFormData>(emptyWound);
  const [incidentFormData, setIncidentFormData] = useState<IncidentFormData>(emptyIncident);
  const [summaryFormData, setSummaryFormData] = useState<string>('');
  const [measurementsFormData, setMeasurementsFormData] = useState<MeasurementsFormData>(emptyMeasurements);
  const [respiratoryFormData, setRespiratoryFormData] = useState<RespiratoryFormData>(emptyRespiratory);
  const [seizureFormData, setSeizureFormData] = useState<SeizureFormData>(() => emptySeizure(defaultObservedTime(visitWindow)));
  const [outputFormData, setOutputFormData] = useState<OutputFormData>(emptyOutput);
  const [supervisoryFormData, setSupervisoryFormData] = useState<SupervisoryFormData>(emptySupervisory);
  const [therapyFormData, setTherapyFormData] = useState<TherapyFormData>(emptyTherapy);
  const [aideTasksFormData, setAideTasksFormData] = useState<AideTasksFormData>(emptyAideTasks);
  const [templateNoteFormData, setTemplateNoteFormData] = useState<TemplateNoteFormData>(emptyTemplateNote);

  const formBundle: EntryFormBundle = useMemo(() => ({
    vitals: vitalFormData,
    mar: marFormData,
    feed: feedFormData,
    notes: notesFormData,
    wound: woundFormData,
    incident: incidentFormData,
    summary: summaryFormData,
    measurements: measurementsFormData,
    respiratory: respiratoryFormData,
    seizure: seizureFormData,
    output: outputFormData,
    supervisory: supervisoryFormData,
    therapy: therapyFormData,
    aidetasks: aideTasksFormData,
    templatenote: templateNoteFormData
  }), [vitalFormData, marFormData, feedFormData, notesFormData, woundFormData, incidentFormData, summaryFormData,
    measurementsFormData, respiratoryFormData, seizureFormData, outputFormData, supervisoryFormData, therapyFormData, aideTasksFormData, templateNoteFormData]);

  // A change to any form makes the last server field errors stale
  useEffect(() => {
    setServerFieldErrors((prev) => (Object.keys(prev).length ? {} : prev));
  }, [formBundle]);

  // Load entries when patient ID and active visit ID are available
  useEffect(() => {
    if (patientId && activeVisitId) {
      loadEntries(patientId, activeVisitId);
      setHasActiveVisit(true);
    } else {
      setCurrentVisitEntries([]);
      setHasActiveVisit(false);
    }
    if (patientId) {
      fetchMedications(patientId)
        .then(meds => {
          setPatientMedications(meds.filter(m => m.status === 'ACTIVE'));
        })
        .catch(() => {
          setPatientMedications([]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, activeVisitId]);

  // Phase 18: visit type (supervisory note, F21), discipline (quick-add order) and times (observed default)
  useEffect(() => {
    if (!patientId || !chartVisitId || visitInfoById[chartVisitId]) return undefined;
    let cancelled = false;
    fetchVisitById(patientId, chartVisitId)
      .then((visit) => {
        if (cancelled || !visit) return;
        setVisitInfoById((prev) => ({
          ...prev,
          [chartVisitId]: {
            visitId: chartVisitId,
            visitType: visit.visitType || null,
            discipline: visit.discipline || null,
            startTime: visit.startTime || null,
            endTime: visit.endTime || null
          }
        }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [patientId, chartVisitId, visitInfoById]);

  const storeChartResponse = (visitId: string, response: { chart?: ChartMeta; lastGivenByMedication?: Record<string, string> }) => {
    if (response.chart) setChartMetaByVisit(prev => ({ ...prev, [visitId]: response.chart as ChartMeta }));
    setLastGivenByVisit(prev => ({ ...prev, [visitId]: response.lastGivenByMedication || {} }));
  };

  // Function to load entries from the API
  const loadEntries = async (patientId: string, visitId: string) => {
    // Phase 18U U4: the spinner is for the first load only; a reload keeps the list on screen
    const firstLoad = loadedVisitRef.current !== visitId;
    try {
      if (firstLoad) {
        // Another visit (or patient): never show the previous visit's entries while loading
        setCurrentVisitEntries([]);
        setLoadingEntries(true);
      }
      const response = await fetchChartEntriesWithStatus(patientId, visitId);
      storeChartResponse(visitId, response);
      setCurrentVisitEntries(sortEntriesByDateDesc(response.chartEntries.map((e) => toLocalEntry(e, visitId))));
      loadedVisitRef.current = visitId;
    } catch (error) {
      console.error("Error loading charting entries");
    } finally {
      if (firstLoad) setLoadingEntries(false);
    }
  };

  // Function to load historical visit entries
  const loadHistoricalVisitEntries = async (patientId: string, visitId: string | number, forceReload: boolean = false): Promise<'DRAFT' | 'SUBMITTED' | 'QA' | 'COMPLETE' | null> => {
    try {
      // Skip if we already loaded this visit's entries, unless forceReload is true
      if (!forceReload && historicalVisitEntries[visitId] && historicalVisitEntries[visitId].length > 0) {
        return null; // Return null to indicate we used cached data
      }

      // Use fetchChartEntriesWithStatus to get both entries AND status in one call
      const response = await fetchChartEntriesWithStatus(patientId, String(visitId));
      storeChartResponse(String(visitId), response);

      setHistoricalVisitEntries(prev => ({
        ...prev,
        [visitId]: sortEntriesByDateDesc(response.chartEntries.map((e) => toLocalEntry(e, String(visitId))))
      }));

      // Return the charting status so the component can use it
      return response.chartingStatus as 'DRAFT' | 'SUBMITTED' | 'QA' | 'COMPLETE';
    } catch (error) {
      console.error('[useChartingData] Error loading historical visit entries');
      return null;
    }
  };

  // Entries of the visit on screen (copy last, rescue med link, PRN follow-ups)
  const visitEntries: Entry[] = useMemo(
    () => (selectedVisitId === 'current' ? currentVisitEntries : historicalVisitEntries[selectedVisitId] || []),
    [selectedVisitId, currentVisitEntries, historicalVisitEntries]
  );
  const lastGivenByMedication = (chartVisitId && lastGivenByVisit[chartVisitId]) || {};

  const reloadVisit = () => {
    if (selectedVisitId === 'current') {
      loadEntries(patientId, activeVisitId!);
    } else {
      loadHistoricalVisitEntries(patientId, selectedVisitId, true); // Force reload
    }
  };

  /** Phase 18 M1: client validation of the form of a local entry type */
  const getFormErrors = useCallback(
    (type: string | null | undefined): EntryErrors => validateEntryForm(type, formBundle, { medications: patientMedications }),
    [formBundle, patientMedications]
  );

  /**
   * Save the open form of `type` as a new entry, or as a change to `editId`.
   * Phase 18 M1: only filled fields are sent; a 400 ENTRY_INVALID maps `fields[]` to inline errors.
   */
  const submitEntry = async (type: string, editId: string | number | null = null, amendReason?: string) => {
    if (!patientId || (!activeVisitId && selectedVisitId === 'current')) {
      console.error("Cannot save entry: Missing patient ID or visit ID");
      return;
    }

    const visitId = selectedVisitId === 'current' ? activeVisitId! : String(selectedVisitId);
    setServerFieldErrors({});

    try {
      // Convert the local type to API's EntryType
      const apiType = convertEntryTypeToApi(type) as EntryType;
      const entryData: EntryData = buildEntryData(type, formBundle, { window: chartWindow });

      // Phase 16C (C7): observed time, limited to the visit window (the server checks it again).
      // MAR uses its administration time and a seizure its start time.
      const clock = type === 'mar' ? marFormData.administrationTime : type === 'seizure' ? seizureFormData.startedTime : observedTime;
      const sendObserved = !editId || type === 'mar' || type === 'seizure';
      const observedAt = clock ? observedIsoFromTime(clock, chartWindow) : null;
      if (sendObserved && clock) {
        const problem = observedProblem(observedAt, chartWindow);
        if (problem) {
          setObservedError(problem);
          throw inlineError(problem);
        }
      }
      setObservedError(null);

      const entryPayload: CreateEntryPayload = {
        entryType: apiType,
        data: entryData,
        ...(editId && amendReason ? { amendReason } : {}),
        ...(sendObserved && observedAt ? { observedAt } : {})
      };

      let response;
      if (editId) {
        // Update existing entry; expectedVersion = the version being edited (16B B6)
        const edited = [...currentVisitEntries, ...Object.values(historicalVisitEntries).flat()].find((e) => String(e.id) === String(editId));
        response = await updateChartEntry(patientId, visitId, String(editId), entryPayload, { expectedVersion: edited?.editVersion });
      } else {
        // Phase 16C: an enrolled phone queues offline entries in the encrypted outbox (inside the
        // service); a laptop or a phone without offline mode gets "You're offline — reconnect to
        // continue" and the form keeps its text in memory only (no pelora-drafts any more)
        response = await createChartEntry(patientId, visitId, entryPayload);
      }

      // Phase 18U U4: show the saved entry at once, then reconcile with the server's list
      const saved = response as ChartEntry | undefined;
      if (saved?.id && (saved.type || saved.entryType)) {
        const local = toLocalEntry(saved, visitId);
        if (selectedVisitId === 'current') setCurrentVisitEntries((prev) => upsertEntry(prev, local));
        else setHistoricalVisitEntries((prev) => ({ ...prev, [selectedVisitId]: upsertEntry(prev[selectedVisitId] || [], local) }));
      }
      reloadVisit();

      return response?.syncState === 'pending' ? { ...response, queued: true } : response;
    } catch (error) {
      console.error('Error submitting entry');
      if (hasApiErrorCode(error, 'ENTRY_INVALID')) {
        const fields = getFieldErrors(error);
        if (fields.observedAt) setObservedError(fields.observedAt);
        setServerFieldErrors(fields);
      } else {
        // 400 TIME_ORDER (16B): the observed time is outside the visit window
        const fields = getFieldErrors(error);
        if (fields.observedAt) setObservedError(fields.observedAt);
      }
      throw error;
    }
  };

  // Function to flag an entry
  const handleFlagEntry = async (entryId: string | number) => {
    if (!patientId || (!activeVisitId && selectedVisitId === 'current')) {
      console.error("Cannot flag entry: Missing patient ID or visit ID");
      throw new Error("Cannot flag entry: Missing patient ID or visit ID");
    }

    const visitId = selectedVisitId === 'current' ? activeVisitId! : String(selectedVisitId);

    try {
      await flagChartEntry(patientId, visitId, String(entryId));
      reloadVisit();
    } catch (error) {
      console.error("Error flagging entry");
      throw error; // Propagate error to be handled by UI
    }
  };

  // Function to invalidate an entry
  const handleInvalidateEntry = async (entryId: string | number, invalidationReason: string) => {
    if (!patientId || (!activeVisitId && selectedVisitId === 'current')) {
      console.error("Cannot invalidate entry: Missing patient ID or visit ID");
      throw new Error("Cannot invalidate entry: Missing patient ID or visit ID");
    }

    const visitId = selectedVisitId === 'current' ? activeVisitId! : String(selectedVisitId);

    try {
      await invalidateChartEntry(patientId, visitId, String(entryId), invalidationReason);
      reloadVisit();
    } catch (error) {
      console.error("Error invalidating entry");
      throw error; // Propagate error to be handled by UI
    }
  };

  /**
   * Phase 18 M6: a supervisor changes the visibility of another author's note or incident.
   * Only `visibility` (and `internalOnly` on an incident) is sent, so the server sees a visibility-only change.
   */
  const changeEntryVisibility = async (entry: Entry, visibility: EntryVisibility, reason: string) => {
    const visitId = String(entry.visitId || chartVisitId || '');
    if (!patientId || !visitId) throw new Error('Cannot change visibility: Missing patient ID or visit ID');
    // Only visibility (and the INCIDENT internalOnly alias): the server treats anything more as an edit
    const data = {
      visibility,
      ...(entry.type === 'incident' ? { internalOnly: visibility === 'INTERNAL' } : {})
    } as unknown as EntryData;
    await updateChartEntry(
      patientId,
      visitId,
      String(entry.id),
      { entryType: convertEntryTypeToApi(entry.type) as EntryType, data, reason, amendReason: reason },
      { expectedVersion: entry.editVersion }
    );
    reloadVisit();
  };

  /**
   * Completely resets all form data and edit state
   */
  const resetFormData = () => {
    const clock = defaultClock();
    setVitalFormData(emptyVitals());
    setMarFormData(emptyMar(clock));
    setFeedFormData(emptyFeed());
    setNotesFormData(emptyNote());
    setWoundFormData(emptyWound());
    setIncidentFormData(emptyIncident());
    setSummaryFormData('');
    setMeasurementsFormData(emptyMeasurements());
    setRespiratoryFormData(emptyRespiratory());
    setSeizureFormData(emptySeizure(clock));
    setOutputFormData(emptyOutput());
    setSupervisoryFormData(emptySupervisory());
    setTherapyFormData(emptyTherapy());
    setAideTasksFormData(emptyAideTasks());
    setTemplateNoteFormData(emptyTemplateNote());

    // Clear editing state
    setEditingEntryId(null);
    setObservedTime(clock);
    setObservedError(null);
    setServerFieldErrors({});
  };

  // Function to populate form data based on entry type for editing
  const populateFormData = (entry: Entry) => {
    const clock = defaultClock();
    switch (entry.type) {
      case 'vitals': setVitalFormData(vitalsForm(entry)); break;
      case 'mar': setMarFormData(marForm(entry, clock)); break;
      case 'feed': setFeedFormData(feedForm(entry)); break;
      case 'notes': setNotesFormData(noteForm(entry)); break;
      case 'wound': setWoundFormData(woundForm(entry)); break;
      case 'incident': setIncidentFormData(incidentForm(entry)); break;
      case 'summary': setSummaryFormData(summaryForm(entry)); break;
      case 'measurements': setMeasurementsFormData(measurementsForm(entry)); break;
      case 'respiratory': setRespiratoryFormData(respiratoryForm(entry)); break;
      case 'seizure': setSeizureFormData(seizureForm(entry, clock)); break;
      case 'output': setOutputFormData(outputForm(entry)); break;
      case 'supervisory': setSupervisoryFormData(supervisoryForm(entry)); break;
      case 'therapy': setTherapyFormData(therapyForm(entry)); break;
      case 'aidetasks': setAideTasksFormData(aideTasksForm(entry)); break;
      default: {
        // Phase 20E: note templates; otherwise an unknown type leaves the form data alone
        const template = noteTemplateFor(entry.type);
        if (template) setTemplateNoteFormData(templateNoteForm(template, entry.data));
        break;
      }
    }
  };

  /** Phase 18 M8: the latest entry of the type in the visit (not invalidated) */
  const hasLast = useCallback((type: string) => Boolean(latestEntryOfType(visitEntries, type)), [visitEntries]);

  /** Phase 18 M8 "Copy last": prefill the open form from the latest entry of its type in the visit */
  const copyLast = (type: string): boolean => {
    const last = latestEntryOfType(visitEntries, type);
    if (!last) return false;
    switch (type) {
      case 'vitals': setVitalFormData(vitalsForm(last)); break;
      case 'respiratory': setRespiratoryFormData(respiratoryForm(last)); break;
      case 'feed': setFeedFormData(feedForm(last)); break;
      case 'output': setOutputFormData(outputForm(last)); break;
      default: return false;
    }
    return true;
  };

  /** A new entry form starts from a fresh observed default (now, or the visit end after it) */
  const prepareNewEntry = () => {
    const clock = defaultClock();
    setObservedTime(clock);
    setObservedError(null);
    setMarFormData((prev) => ({ ...prev, administrationTime: clock }));
    setSeizureFormData((prev) => ({ ...prev, startedTime: clock }));
  };

  /** Phase 18 M7 "Record effect": the MAR form, prefilled as a follow-up of a PRN GIVEN entry */
  const startFollowUp = (entry: Entry) => {
    setMarFormData(marFollowUpForm(entry, defaultClock()));
  };

  return {
    // State
    currentVisitEntries,
    historicalVisitEntries,
    hasActiveVisit,
    loadingEntries,
    editingEntryId,
    inPlaceEditingEntryId,
    selectedVisitId,
    patientMedications,
    chartMetaByVisit,
    observedTime,
    setObservedTime,
    observedError,
    // Phase 18
    visitEntries,
    lastGivenByMedication,
    chartVisit,
    chartWindow,
    serverFieldErrors,
    formBundle,

    // Form data
    vitalFormData,
    marFormData,
    feedFormData,
    notesFormData,
    woundFormData,
    incidentFormData,
    summaryFormData,
    measurementsFormData,
    respiratoryFormData,
    seizureFormData,
    outputFormData,
    supervisoryFormData,
    therapyFormData,
    aideTasksFormData,
    templateNoteFormData,

    // Setters
    setEditingEntryId,
    setInPlaceEditingEntryId,
    setSelectedVisitId,
    setVitalFormData,
    setMarFormData,
    setFeedFormData,
    setNotesFormData,
    setWoundFormData,
    setIncidentFormData,
    setSummaryFormData,
    setMeasurementsFormData,
    setRespiratoryFormData,
    setSeizureFormData,
    setOutputFormData,
    setSupervisoryFormData,
    setTherapyFormData,
    setAideTasksFormData,
    setTemplateNoteFormData,

    // Functions
    loadEntries,
    loadHistoricalVisitEntries,
    submitEntry,
    getFormErrors,
    handleFlagEntry,
    handleInvalidateEntry,
    changeEntryVisibility,
    resetFormData,
    populateFormData,
    copyLast,
    hasLast,
    prepareNewEntry,
    startFollowUp,
    sortEntriesByDateDesc // Expose the sorting function
  };
};
